import {
  TextField,
  SelectField,
  FormProvider,
  Flex,
  Heading,
  Text,
  Divider,
  Button,
  FiSearch,
  Spinner,
  ReadOnlyField,
} from '@agrotoken-tech/ui';
import { SectionGrid } from '../../SectionGrid';
import { useTranslation } from '@bcba/react-hooks';
import { Exchange, exchanges } from '@bcba/types';
import useFirstStepForm from './hooks/useFirstStepForm';
import { FormStep } from './utils/form-step.interface';

export const FirstStepForm = ({ setStep, stepperJSX }: FormStep) => {
  const { t } = useTranslation();

  const {
    handleSubmit,
    submitHandler,
    formMethods,
    dropdownProvincesOptions,
    hasSelectedProvince,
    getProvinceLocalitiesOptions,
    companyTypesOptions,
    defaultLocalityValueFromPreviousSelection,
    afipResponseIsLoading,
    companyName,
    showCompanyNameFieldReadOnly,
  } = useFirstStepForm({ setStep, stepperJSX });

  return (
    <Flex
      w="full"
      px="48px"
      as="form"
      noValidate
      flexDir={'column'}
      onSubmit={handleSubmit(submitHandler)}
    >
      <Heading size="md" mb="12px">
        {t('businessSignUp.firstStep.title')}
      </Heading>
      {stepperJSX}
      <Text size="xl" variant="medium" mt="36px" mb="12px">
        {t('businessSignUp.firstStep.stepIndicator')}
      </Text>
      <Divider />
      <FormProvider {...formMethods}>
        <SectionGrid w="full" mt="24px">
          <TextField
            name="companyFiscalId"
            label={t('businessSignUp.form.cuit.label')}
            placeholder={t('businessSignUp.form.cuit.placeholder')}
            errorMessageFormatter={t}
            type="number"
            rhfOptions={{ required: true }}
          />
          <Flex>
            {showCompanyNameFieldReadOnly ? (
              <>
                <ReadOnlyField
                  label={t('businessSignUp.form.businessName.label')}
                  value={companyName}
                />
                {afipResponseIsLoading && (
                  <Flex ml="12px" alignItems="end" mb={2}>
                    <Spinner />
                  </Flex>
                )}
              </>
            ) : (
              <TextField
                name="companyName"
                label={t('businessSignUp.form.businessName.label')}
                placeholder={t('businessSignUp.form.businessName.placeholder')}
                errorMessageFormatter={t}
                rhfOptions={{ required: true }}
              />
            )}
          </Flex>
          <TextField
            name="companyStreetName"
            label={t('businessSignUp.form.addressStreet.label')}
            placeholder={t('businessSignUp.form.addressStreet.placeholder')}
            errorMessageFormatter={t}
            rhfOptions={{ required: true }}
          />
          <TextField
            name="companyStreetNumber"
            label={t('businessSignUp.form.addressNumber.label')}
            placeholder={t('businessSignUp.form.addressNumber.placeholder')}
            errorMessageFormatter={t}
            rhfOptions={{ required: true }}
            type="number"
          />

          <SelectField
            name="companyProvinceUuid"
            label={t('businessSignUp.form.state.label')}
            placeholder={t('businessSignUp.form.state.placeholder')}
            errorMessageFormatter={t}
            options={dropdownProvincesOptions}
            rhfOptions={{ required: true }}
            defaultInputValue={formMethods.watch('companyProvinceName')}
          />
          <SelectField
            name="companyLocalityId"
            label={t('businessSignUp.form.locality.label')}
            placeholder={t('businessSignUp.form.locality.placeholder')}
            errorMessageFormatter={t}
            rhfOptions={{ required: true }}
            options={defaultLocalityValueFromPreviousSelection}
            isDisabled={!hasSelectedProvince}
            leftIcon={FiSearch}
            debounce={500}
            getOptions={getProvinceLocalitiesOptions}
            noOptionMessage={t('businessSignUp.form.locality.noOptions')}
            defaultInputValue={formMethods.watch('companyLocalityName')}
            isClearable
            cacheOptions={false}
          />
          <TextField
            name="companyPostalCode"
            label={t('businessSignUp.form.zipCode.label')}
            placeholder={t('businessSignUp.form.zipCode.placeholder')}
            errorMessageFormatter={t}
            rhfOptions={{ required: true }}
            type="number"
          />
          <SelectField
            name="companyExchange"
            label={t('businessSignUp.form.exchanges.label')}
            placeholder={t('businessSignUp.form.exchanges.placeholder')}
            errorMessageFormatter={t}
            options={exchanges.map((value: Exchange) => ({
              label: t(`exchange.${value}`),
              value,
            }))}
            isSearchable={false}
            rhfOptions={{ required: true }}
          />
          <SelectField
            name="companyType"
            label={t('businessSignUp.form.businessType.label')}
            placeholder={t('businessSignUp.form.businessType.placeholder')}
            errorMessageFormatter={t}
            isSearchable={false}
            options={companyTypesOptions}
            rhfOptions={{ required: true }}
          />
        </SectionGrid>
      </FormProvider>
      <Flex w="full" justifyContent={'flex-end'}>
        <Button type="submit" variant={'primary'}>
          {t('businessSignUp.form.next')}
        </Button>
      </Flex>
    </Flex>
  );
};
